"use client";

import { useRef, useState, useEffect, type ReactNode, type RefObject } from "react";
import Link from "next/link";

/* ─── Scroll reveal ───────────────────────────────────────── */
function useVisible<T extends HTMLElement = HTMLDivElement>(threshold = 0.1): [RefObject<T>, boolean] {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } },
      { threshold }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Reveal({ children, delay = 0, from = "bottom" }: { children: ReactNode; delay?: number; from?: "bottom" | "left" | "right" }) {
  const [ref, v] = useVisible();
  const hidden: Record<"bottom" | "left" | "right", string> = {
    bottom: "opacity-0 translate-y-7",
    left:   "opacity-0 -translate-x-7",
    right:  "opacity-0 translate-x-7",
  };
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${v ? "opacity-100 translate-x-0 translate-y-0" : hidden[from]}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

/* ─── Shared UI ───────────────────────────────────────────── */
function Eyebrow({ text }: { text: string }) {
  return <span className="block text-[11px] font-bold tracking-[0.15em] uppercase text-blue-700 mb-3.5">{text}</span>;
}
function Divider() {
  return <div className="w-11 h-[3px] bg-blue-700 mb-6" />;
}
function CheckIcon() {
  return (
    <div className="w-5 h-5 bg-blue-700 flex-shrink-0 flex items-center justify-center mt-0.5">
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );
}

/* ─── Data ────────────────────────────────────────────────── */
const SERVICES = [
  {
    n: "01", title: "Cloud Migration",
    body: "Seamless, zero-downtime migration of applications, databases and infrastructure to AWS, Azure, or Google Cloud. Secure data migration and performance optimisation post-migration.",
    img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&q=80",
    tags: ["Zero downtime", "AWS / Azure / GCP", "Performance optimised"],
  },
  {
    n: "02", title: "Cloud Infrastructure Setup",
    body: "Design and deployment of scalable, resilient cloud infrastructure — Infrastructure as Code (Terraform, Ansible), auto-scaling, load balancing, multi-region deployment and high availability architecture.",
    img: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&q=80",
    tags: ["Terraform / Ansible", "Auto-scaling", "Multi-region"],
  },
  {
    n: "03", title: "Cloud Security & Compliance",
    body: "Multi-layered cloud security — data encryption, IAM, Web Application Firewall (WAF), DDoS protection, continuous threat monitoring and compliance frameworks (ISO 27001, SOC 2, GDPR).",
    img: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&q=80",
    tags: ["IAM & WAF", "ISO 27001 / SOC 2", "DDoS protection"],
  },
  {
    n: "04", title: "Hybrid Cloud Solutions",
    body: "Best-of-both-worlds environments combining public and private cloud. Multi-cloud strategy, private cloud setup, on-premises integration and unified management for maximum operational flexibility.",
    img: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=600&q=80",
    tags: ["Multi-cloud", "On-premises integration", "Unified management"],
  },
  {
    n: "05", title: "Cloud Data & Analytics",
    body: "Big data processing, real-time analytics dashboards, data lakes, machine learning pipelines and business intelligence — powered by cloud-native services like BigQuery, Redshift and Azure Synapse.",
    img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80",
    tags: ["BigQuery / Redshift", "Data lakes", "ML pipelines"],
  },
  {
    n: "06", title: "Managed Cloud Services",
    body: "Full end-to-end cloud management — 24/7 performance monitoring, cost optimisation (typically 30–40% savings), automated scaling, security management and dedicated technical support.",
    img: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&q=80",
    tags: ["24/7 monitoring", "30–40% cost savings", "Dedicated support"],
  },
];

const PLATFORMS = [
  { name: "Amazon Web Services (AWS)", services: "EC2, S3, RDS, Lambda, EKS, CloudFront, Route 53", logo: "AWS" },
  { name: "Microsoft Azure",           services: "Virtual Machines, Blob Storage, Azure SQL, AKS, Active Directory", logo: "Azure" },
  { name: "Google Cloud Platform (GCP)",services: "Compute Engine, Cloud Run, BigQuery, GKE, Cloud SQL", logo: "GCP" },
  { name: "IBM Cloud",                  services: "OpenShift, Watson AI, IBM Db2, Cloud Object Storage", logo: "IBM" },
];

const BENEFITS = [
  { title: "Cost Optimisation",    body: "Pay only for what you use. Eliminate idle infrastructure costs — typical savings of 30–50%" },
  { title: "Infinite Scalability",  body: "Scale resources up or down in minutes based on real business demand — no over-provisioning" },
  { title: "Enhanced Security",     body: "Enterprise-grade protection exceeding most on-premises security capabilities" },
  { title: "Business Continuity",   body: "Automated backups, disaster recovery and 99.99% uptime SLAs across all major platforms" },
  { title: "Global Reach",          body: "Deploy to multiple regions worldwide for low-latency performance and regulatory compliance" },
  { title: "Innovation Speed",      body: "Launch new products and features 3–5x faster with scalable, on-demand cloud infrastructure" },
];

const STEPS = [
  { n: "01", title: "Infrastructure Assessment",  body: "Evaluate your current environment, workloads, dependencies and business objectives to define the migration scope." },
  { n: "02", title: "Cloud Architecture Design",  body: "Design a tailored cloud architecture — platform selection, network topology, security controls and cost projections." },
  { n: "03", title: "Migration Execution",         body: "Staged, zero-downtime migration using blue-green deployments, parallel running and incremental cutover strategies." },
  { n: "04", title: "Security Hardening",          body: "Implement IAM policies, encryption, WAF, network segmentation and compliance controls on the new cloud environment." },
  { n: "05", title: "Performance Optimisation",   body: "Right-size instances, configure auto-scaling, tune databases and implement caching to maximise performance and reduce cost." },
  { n: "06", title: "Managed Operations",          body: "Ongoing 24/7 monitoring, incident response, cost governance and continuous improvement of your cloud environment." },
];

const FAQS = [
  { q: "Which cloud platform should we choose?",    a: "AWS, Azure and GCP each have distinct strengths. AWS leads on breadth of services, Azure integrates tightly with Microsoft tools, GCP excels in data and ML. We recommend based on your existing tech stack, compliance requirements and workload characteristics." },
  { q: "Will migration cause downtime?",             a: "Not if planned properly. We use blue-green deployments, parallel running and staged cutover strategies to ensure zero or minimal downtime throughout the migration process." },
  { q: "How much does cloud migration cost?",        a: "Cost depends on data volume, application complexity and downtime tolerance. We provide a detailed cost estimate after an infrastructure assessment — no hidden fees." },
  { q: "How do we reduce cloud costs after migration?", a: "Through right-sizing instances, using reserved or spot instances, optimising storage tiers, removing idle resources and configuring auto-scaling policies. Typical savings: 30–50% vs. initial cloud spend." },
  { q: "How secure is cloud infrastructure?",        a: "Major cloud platforms have enterprise-grade security that often exceeds on-premises capabilities. We implement additional controls — VPCs, encryption at rest and in transit, IAM, WAF and continuous threat monitoring." },
  { q: "What is a hybrid cloud and do we need one?", a: "A hybrid cloud combines public cloud (AWS/Azure/GCP) with private cloud or on-premises infrastructure. It is ideal for businesses with regulatory requirements, legacy systems, or data sovereignty needs that prevent full public cloud migration." },
];

/* ─── FAQ Item ────────────────────────────────────────────── */
function FaqItem({ q, a, i }: { q: string; a: string; i: number }) {
  const [open, setOpen] = useState(false);
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`border-b border-slate-200 transition-all duration-500 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
      style={{ transitionDelay: `${i * 0.06}s` }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left flex justify-between items-center gap-4 py-[22px] bg-transparent border-none cursor-pointer"
      >
        <span className="text-[15px] font-semibold text-slate-900 leading-snug">{q}</span>
        <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center transition-colors duration-200 ${open ? "bg-blue-700" : "bg-slate-100"}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d={open ? "M2 6h8" : "M6 2v8M2 6h8"} stroke={open ? "#fff" : "#1565C0"} strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </div>
      </button>
      {open && (
        <div className="pb-[22px]">
          <p className="text-[14px] text-slate-500 leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

/* ─── Service Card ────────────────────────────────────────── */
function ServiceCard(props: { s: { tags: string[]; img: string; title: string; body: string; n: string }; i: number }) {
  const { s, i } = props;
  const [ref, v] = useVisible();
  const [hov, setHov] = useState(false);
  return (
    <div
      ref={ref}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      className={`transition-all duration-500 flex flex-col overflow-hidden bg-white border ${hov ? "border-slate-300 shadow-xl" : "border-slate-200 shadow-sm"}`}
      style={{ transitionDelay: `${(i % 3) * 0.09}s`, opacity: v ? 1 : 0, transform: v ? "none" : "translateY(24px)" }}
    >
      <div className="relative h-[170px] overflow-hidden">
        <img
          src={s.img}
          alt={s.title}
          className={`w-full h-full object-cover transition-transform duration-500 ${hov ? "scale-105" : "scale-100"}`}
        />
        <div className="absolute top-3.5 left-3.5 bg-blue-700 text-white text-[11px] font-bold tracking-[0.08em] px-2.5 py-1">
          {s.n}
        </div>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <p className="text-[14px] font-bold text-slate-900 mb-2.5 leading-snug">{s.title}</p>
        <p className="text-[13.5px] text-slate-500 leading-relaxed mb-4">{s.body}</p>
        <div className="flex flex-wrap gap-1.5 mt-auto">
          {s.tags.map(tag => (
            <span key={tag} className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 tracking-wide">{tag}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Step ────────────────────────────────────────────────── */
function Step(props: { s: { n: string; title: string; body: string }; i: number; last?: boolean }) {
  const { s, i, last } = props;
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`flex gap-4 items-start transition-all duration-500 relative ${!last ? "pb-7" : ""}`}
      style={{ transitionDelay: `${(i % 3) * 0.09}s`, opacity: v ? 1 : 0, transform: v ? "none" : "translateY(20px)" }}
    >
      {!last && <div className="absolute left-[17px] top-9 w-px bottom-0 bg-slate-200" />}
      <div className="w-9 h-9 bg-blue-700 flex-shrink-0 flex items-center justify-center">
        <span className="text-[11px] font-bold text-white">{s.n}</span>
      </div>
      <div className="pt-1.5">
        <p className="text-[14px] font-bold text-slate-900 mb-1.5">{s.title}</p>
        <p className="text-[13.5px] text-slate-500 leading-relaxed">{s.body}</p>
      </div>
    </div>
  );
}

/* ─── Platform Card ───────────────────────────────────────── */
function PlatformCard(props: { p: { logo: string; name: string; services: string }; i:number }) {
  const { p, i } = props;
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className="bg-white border border-slate-200 p-6 transition-all duration-500"
      style={{ transitionDelay: `${i * 0.1}s`, opacity: v ? 1 : 0, transform: v ? "none" : "translateY(20px)" }}
    >
      {/* Logo badge */}
      <div className="inline-flex items-center justify-center bg-blue-700 text-white text-[11px] font-black tracking-widest px-3 py-1.5 mb-4">
        {p.logo}
      </div>
      <p className="text-[14px] font-bold text-slate-900 mb-2 leading-snug">{p.name}</p>
      <p className="text-[13px] text-slate-500 leading-relaxed">{p.services}</p>
    </div>
  );
}

/* ─── Page ────────────────────────────────────────────────── */
export default function CloudPage() {
  return (
    <main className="bg-white">

      {/* ══ HERO ═══════════════════════════════════════════ */}
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-0 lg:min-h-[580px]">
        <div className="flex flex-col justify-center px-4 sm:px-8 lg:px-20 py-[30px] bg-white">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-slate-400 mb-6">
            <Link href="/services" className="text-blue-700 font-medium hover:underline">Services</Link>
            <span className="text-slate-300">›</span>
            <Link href="/services/it" className="text-blue-700 font-medium hover:underline">IT Solutions</Link>
            <span className="text-slate-300">›</span>
            <span>Cloud Solutions</span>
          </nav>

          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-5">
            {["AWS", "Azure", "GCP", "Migration", "DevOps", "Managed Cloud"].map(tag => (
              <span key={tag} className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 tracking-wide">{tag}</span>
            ))}
          </div>

          <Eyebrow text="IT Solutions — Cloud Services" />
          <Divider />

          <h1 className="text-[clamp(26px,3.5vw,46px)] font-extrabold text-slate-900 leading-[1.1] mb-5 ">
            Scale Faster. Innovate Boldly. Operate Smarter.
          </h1>
          <p className="text-[16px] font-light text-slate-500 leading-relaxed mb-9 ">
            Harness the full power of cloud computing with Farsight&apos;s comprehensive cloud services — from seamless migration to advanced analytics, hybrid environments and 24/7 managed infrastructure.
          </p>
          <div className="flex gap-3.5">
            <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-7 py-3.5 hover:bg-blue-800 transition-colors border-0 cursor-pointer">
              START CLOUD JOURNEY
            </button>
            <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-7 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
              GET FREE ASSESSMENT
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden">
          <img
            src="/images/services/IT/Banner/cloud.png"
            alt="Cloud solutions"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Blue stat strip */}
      <div className="bg-blue-700 grid grid-cols-2 lg:grid-cols-4">
        {[
          { v: "4",       l: "Cloud Platforms" },
          { v: "0",       l: "Downtime Migration" },
          { v: "30–50%",  l: "Cost Savings" },
          { v: "24/7",    l: "Managed Support" },
        ].map((s, i) => (
          <div key={i} className={`py-5 text-center ${i < 3 ? "border-r border-white/15" : ""}`}>
            <p className="text-[22px] font-black text-white leading-none">{s.v}</p>
            <p className="text-[11px] font-medium text-white/65 mt-1.5 tracking-widest uppercase">{s.l}</p>
          </div>
        ))}
      </div>

      {/* ══ SERVICES GRID — 3×2 ════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-10 lg:mb-14">
              <Eyebrow text="Cloud Services" />
              <Divider />
              <div className="flex justify-between items-end flex-wrap gap-4">
                <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight m-0">
                  Six Cloud Capabilities.<br />One Expert Team.
                </h2>
                <p className="text-[14px] text-slate-500 max-w-[360px] leading-relaxed">
                  End-to-end cloud services — from first migration to continuous optimisation and managed operations.
                </p>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => <ServiceCard key={i} s={s} i={i} />)}
          </div>
        </div>
      </section>

      {/* ══ PLATFORMS — 2-col text + 2×2 grid ════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Cloud Platforms We Support" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Multi-Cloud Expertise Across All Major Providers
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-8">
              We are platform-agnostic — our architects work with AWS, Azure, GCP and IBM Cloud. We recommend the right platform for your specific business needs, or help you manage a multi-cloud strategy.
            </p>
            <img
              src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=700&q=80"
              alt="Cloud platforms"
              className="w-full h-[260px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <div className="grid grid-cols-1 gap-0.5">
              {PLATFORMS.map((p, i) => <PlatformCard key={i} p={p} i={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ BUSINESS BENEFITS ══════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=700&q=80"
                alt="Cloud business benefits"
                className="w-full h-[260px] sm:h-[360px] lg:h-[460px] object-cover"
              />
              {/* Floating stat */}
              <div className="absolute bottom-[-24px] left-[-24px] bg-white border border-slate-200 shadow-xl px-6 py-5 hidden lg:block">
                <p className="text-[30px] font-black text-blue-700 leading-none">30–50%</p>
                <p className="text-[11px] text-slate-400 mt-1.5 tracking-[0.06em] uppercase font-medium">Cost Savings</p>
                <p className="text-[28px] font-black text-orange-600 leading-none mt-4">99.99%</p>
                <p className="text-[11px] text-slate-400 mt-1.5 tracking-[0.06em] uppercase font-medium">Uptime SLA</p>
              </div>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <Eyebrow text="Cloud Business Benefits" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Why Cloud Is Your Best Infrastructure Decision
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-8">
              Cloud infrastructure is no longer just an IT decision — it is a competitive business advantage. Here is what your business gains when you move to the cloud with Farsight.
            </p>
            <div className="flex flex-col gap-5">
              {BENEFITS.map((b, i) => (
                <div key={i} className="flex items-start gap-3.5">
                  <CheckIcon />
                  <div>
                    <p className="text-[14px] font-bold text-slate-900 mb-1">{b.title}</p>
                    <p className="text-[13.5px] text-slate-500 leading-relaxed">{b.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ 6-STEP PROCESS ═════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Migration Process" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              6-Step Cloud Migration Process
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-10">
              A structured, low-risk approach to cloud migration — from initial assessment to fully managed operations. We handle every step so your team can focus on what matters.
            </p>
            <img
              src="https://images.unsplash.com/photo-1563986768609-322da13575f3?w=700&q=80"
              alt="Cloud migration process"
              className="w-full h-[260px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div>
              {STEPS.map((s, i) => <Step key={i} s={s} i={i} last={i === STEPS.length - 1} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ FAQ ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Frequently Asked Questions" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-slate-900 leading-tight mb-6">
              Common Questions About Cloud Solutions
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              Clear answers to the questions we hear most from businesses evaluating cloud migration with Farsight.
            </p>
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=700&q=80"
              alt="Cloud FAQ"
              className="w-full h-[180px] sm:h-[240px] lg:h-[280px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="border-t border-slate-200">
              {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} i={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ CTA ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <video
              src="/videos/CTA.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-[220px] sm:h-[300px] lg:h-[380px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <Eyebrow text="Ready to Move to the Cloud?" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Start Your Cloud Journey Today
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-3">
              Our cloud architects will assess your current infrastructure, identify migration opportunities and provide a detailed roadmap — at no cost, no commitment.
            </p>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              Most clients see 30–50% infrastructure cost savings within the first 6 months of migration.
            </p>
            <div className="flex gap-3.5 flex-wrap">
              <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-8 py-3.5 hover:bg-blue-800 transition-colors border-0 cursor-pointer">
                START CLOUD JOURNEY
              </button>
              <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-8 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
                GET FREE ASSESSMENT
              </button>
            </div>
          </Reveal>
        </div>
      </section>

    </main>
  );
}